import { useLearnSummaryPageQueryOptions } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { showFailureToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";
import { isStudyDataUnavailableError } from "@-/dev.improve.haniverse.lib.error-classification";
import { LearnSummaryPageContent } from "#learn-summary-page-content";
import { LearnSummaryPageErrorView } from "#learn-summary-page-error-view";
import { LearnSummaryPageLoadingFallback } from "#learn-summary-page-loading-fallback";
import { NavContext, useIsActorSyncPreparing } from "@-/dev.improve.haniverse.routes.nav._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useDeviceAccount } from "@-/dev.improve.haniverse.lib.device-settings";
import { useIsSyncingFromCloud } from "@-/dev.improve.haniverse.lib.sync-push.signal";

export function LearnSummaryPageSuspense() {
  const isActorSyncPreparing = useIsActorSyncPreparing();
  const [isInitialPullPending] = useContextSelectors(
    NavContext,
    (context) => context.actorRemoteSync?.isInitialPullComplete === false,
  );
  const [deviceAccount] = useDeviceAccount();
  const isSyncingFromCloud = useIsSyncingFromCloud(deviceAccount !== null);
  const queryOptions = useLearnSummaryPageQueryOptions();
  // Only a device with no usable replica yet (its first sign-in) waits for the account before
  // reading. A returning device reads its local replica at once while the launch pull checks the
  // cloud in the background, so Learn is ready immediately; when that pull brings changes, the
  // query is invalidated and re-reads them.
  const isAwaitingAccount = isActorSyncPreparing;
  // A normal observer starts loading after commit. Cold-start rendering must
  // never fetch/throw a promise during render or depend on a remote bootstrap.
  // This observer is the first-paint gate only: content mounts once data exists and re-reads the
  // same query itself (a cache hit), so nothing is redistributed through a context.
  const {
    data: resolution,
    error,
    isFetching,
    refetch,
  } = useQuery({
    ...queryOptions,
    enabled: !isAwaitingAccount,
    // Re-enabling after the pull must read the account, even if a cached guest
    // result was fresh before the connection changed.
    staleTime: 0,
    // This observer owns the post-auth loading gate. It must receive the reset
    // and refetch while native navigation focus is still settling; otherwise
    // it retains the guest result and redirects as soon as the sync gate opens.
    subscribed: true,
    throwOnError: false,
  });

  if (resolution === undefined) {
    // `throwOnError: false` keeps a failure out of the error boundary, so it must be rendered
    // here: without these branches a rejected resolver looked identical to one still loading.
    if (error !== null && isStudyDataUnavailableError(error)) {
      // Rare, and not fixable by retrying: keep the familiar frame and say what is wrong (and to
      // contact support) when the user actually tries to study.
      return (
        <LearnSummaryPageLoadingFallback
          onPressStartReviewing={() => {
            showFailureToast(error);
          }}
        />
      );
    }
    if (error !== null) {
      return (
        <LearnSummaryPageErrorView
          error={error}
          isRetrying={isFetching}
          onRetry={() => {
            void refetch();
          }}
        />
      );
    }
    return <LearnSummaryPageLoadingFallback />;
  }

  // A cached guest resolution can survive the first signed-in render while the
  // safety backup and account connection are being prepared. Do not redirect
  // that returning learner into the welcome deck: its route would outlive the
  // cache refresh when the account's completed slides arrive.
  // A usable local replica releases the general preparation gate before the
  // initial pull. Its cached guest result still cannot establish that this
  // signed-in account is new, and neither can a replica this device knows is
  // behind. Returning learners with local study data render immediately; only
  // the destructive welcome redirect waits for the pull.
  if (
    resolution.isNewUser &&
    (isAwaitingAccount || isInitialPullPending || isSyncingFromCloud || isFetching)
  ) {
    return <LearnSummaryPageLoadingFallback />;
  }

  return <LearnSummaryPageContent />;
}
